import { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { alpha, useTheme } from '@mui/material';
import { useAppSelector } from 'shared/hooks/redux/hooks';
import { preventDefault, useBox, useCallbacks } from 'shared/hooks/utils';
import { noop } from 'ts-essentials';
import { isMobileApp } from 'utils';
export function useLongPress(delay = 200) {
    const [interval_id, setIntervalId] = useState(null);
    const box_interval = useBox(interval_id);
    const onStart = useCallback((callback) => {
        if (!box_interval.current) {
            setIntervalId(window.setInterval(callback, delay));
        }
    }, []);
    const onEnd = useCallback(() => {
        if (box_interval.current) {
            window.clearInterval(box_interval.current);
            setIntervalId(null);
        }
    }, []);
    return [onStart, onEnd, interval_id];
}
export function useLongPressAction(props) {
    const { animation_name = 'fill', can_turned_off = true, onLongPressEnd, press_animation, background_color, color, active, loading, can_turned_on_again, disabled, fail, } = props;
    const { palette } = useTheme();
    const use_longpress = useAppSelector((state) => state.ui_settings.longpress ?? isMobileApp());
    const default_button_color = useMemo(() => background_color || palette.background.default, [palette]);
    const button_color = useMemo(() => color || palette.primary.main, [color, palette]);
    const text_color = useMemo(() => color === palette.error.main ? color : alpha(palette.action.active, 0.75), [palette, color]);
    const main_animation = useMemo(() => {
        if (press_animation) {
            return press_animation;
        }
        return {
            background: active && !can_turned_on_again
                ? [
                    `linear-gradient(0deg, ${button_color} 0% 100% , ${default_button_color} 0% 0%)`,
                    `linear-gradient(0deg, ${button_color} 0% 0%, ${default_button_color} 0% 100%)`,
                ]
                : [
                    `linear-gradient(0deg, ${button_color} 0% 0%, ${default_button_color} 0% 100%)`,
                    `linear-gradient(0deg, ${button_color} 0% 100% , ${default_button_color} 0% 0%)`,
                ],
            transition: {
                duration: 0.6,
            },
            color: active && !can_turned_on_again
                ? [palette.common.white, text_color]
                : [text_color, palette.common.white],
        };
    }, [
        button_color,
        press_animation,
        text_color,
        palette,
        active,
        can_turned_on_again,
    ]);
    const variants = useMemo(() => ({
        [animation_name]: main_animation,
        active: {
            background: disabled ? alpha(button_color, 0.4) : button_color,
            color: palette.common.white,
        },
        loading: {
            background: [button_color, default_button_color],
            color: [palette.common.white, text_color],
            transition: {
                repeat: Infinity,
                repeatType: 'mirror',
                duration: 0.6,
            },
        },
        disabled: {
            transition: {
                duration: 0.2,
            },
            background: palette.error.main,
            color: active
                ? palette.common.white
                : [text_color, palette.common.white],
        },
        clear: {
            background: disabled
                ? alpha(palette.action.active, 0.1)
                : default_button_color,
            color: disabled ? palette.common.white : text_color,
        },
        fail: {
            transition: {
                duration: 0.2,
            },
            background: palette.error.main,
            color: active
                ? palette.common.white
                : [text_color, palette.common.white],
        },
    }), [
        color,
        palette,
        animation_name,
        main_animation,
        disabled,
        loading,
        text_color,
        active,
        button_color,
        fail,
    ]);
    const [isFirstPress, setIsFirstPress] = useState(true);
    const button_ref = useRef(null);
    const mouseUpHandler = () => {
        setIsFirstPress(true);
    };
    useEffect(() => {
        button_ref.current?.addEventListener('mouseup', mouseUpHandler);
        button_ref.current?.addEventListener('touchend', mouseUpHandler);
        return () => {
            button_ref.current?.removeEventListener('mouseup', mouseUpHandler);
            button_ref.current?.removeEventListener('touchend', mouseUpHandler);
        };
    }, []);
    const onAnimationComplete = useCallback((completed_animation) => {
        if (completed_animation === animation_name) {
            if (!isFirstPress)
                return;
            setIsFirstPress(false);
            return onLongPressEnd();
        }
        return;
    }, [animation_name, onLongPressEnd, isFirstPress]);
    const while_tap_animation = useMemo(() => {
        if (disabled) {
            return 'disabled';
        }
        if (active && !can_turned_off && !can_turned_on_again) {
            return 'active';
        }
        if (loading && !can_turned_on_again) {
            return 'loading';
        }
        if (fail) {
            return 'fail';
        }
        return animation_name;
    }, [
        animation_name,
        active,
        can_turned_off,
        can_turned_on_again,
        disabled,
        loading,
        fail,
    ]);
    const animate = useMemo(() => {
        if (fail) {
            return 'fail';
        }
        if (loading) {
            return 'loading';
        }
        if (active) {
            return 'active';
        }
        return 'clear';
    }, [active, loading, fail]);
    const long_press_handlers = useMemo(() => ({
        onAnimationComplete,
        variants,
        animate,
        initial: false,
        whileTap: while_tap_animation,
        onTap: preventDefault,
        onClick: preventDefault,
        onContextMenu: preventDefault,
        ref: button_ref,
    }), [onAnimationComplete, variants, animate, while_tap_animation]);
    const onClickLongPress = useCallbacks([preventDefault, onLongPressEnd]);
    const default_handlers = useMemo(() => ({
        onClick: disabled ? noop : onClickLongPress,
        variants,
        animate,
    }), [active, variants, animate, onClickLongPress]);
    return use_longpress ? long_press_handlers : default_handlers;
}
